import { CSSProperties, SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";

import { Icon, Modal } from "semantic-ui-react";

import DisplayFileSize from "./DisplayFileSize";

type DisplayContentFileProps = {
  content?: string | null;
  file: File | string;
  isMediaViewer?: boolean;
  showFileSize?: boolean;
};

type Styles = Record<"playVideo", CSSProperties>;

const styles: Styles = {
  playVideo: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "center",
    marginLeft: "-3%",
    position: "absolute",
    width: "100%",
  },
};

const IMAGES = {
  close: "/static/images/chat/image-viewer-close.png",
  playVideo: "/static/images/chat/play-video.png",
};

export const isVideoFile = (filename: string) => {
  const videoRegex = /\.(?:mp4|avi|mov|wmv|flv|webm|mkv|m4v|3gp|ogv|mpg|mpeg|m2v|divx)$/i;

  return videoRegex.test(filename);
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

export const getContentFileName = (content?: string | null) => {
  if (content) {
    const fileMatch = /^file::.*\.\w+$/i.exec(content);
    const imageMatch = /^image::.*\.\w+$/i.exec(content);

    if (fileMatch || imageMatch) {
      return content.replaceAll(/file::|image::/g, "");
    }
  }

  return "";
};

const DisplayContentFile = (props: DisplayContentFileProps) => {
  const [video, setVideo] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(0);

  const videoRef = useRef<HTMLVideoElement>(null);

  const name = useMemo(
    (): string => (props.file instanceof File ? props.file.name : props.file),
    [props.file]
  );

  const fileName = useMemo(() => {
    const formattedName = decodeURI(name.split("?")[0].split("/").pop() || "");

    return getContentFileName(props.content) || formattedName;
  }, [name, props.content]);

  const videoSrc = useMemo(() => {
    if (isVideoFile(fileName)) {
      return props.file instanceof File ? URL.createObjectURL(props.file) : props.file;
    }

    return "";
  }, []);

  useEffect(() => {
    if (!videoRef.current) {
      return () => {
        // ---
      };
    }

    const handleLoadedMetadata = () => {
      setDuration(videoRef.current?.duration ?? 0);
    };

    videoRef.current.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      videoRef.current?.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, []);

  const handleClick = (e: SyntheticEvent) => {
    if (isVideoFile(fileName) && props.isMediaViewer) {
      e.stopPropagation();

      setVideo(videoSrc);
    }
  };

  const handleClose = (e: SyntheticEvent) => {
    e.stopPropagation();

    setVideo(null);
  };

  return (
    <>
      <div className="file" style={{ cursor: "pointer" }} onClick={handleClick}>
        {isVideoFile(fileName) && props.isMediaViewer ? (
          <>
            <video ref={videoRef} src={videoSrc} />
            <div style={styles.playVideo}>
              <img style={{ width: "25px" }} alt="play-video" src={IMAGES.playVideo} />
              <div style={{ color: "white", fontSize: "0.85rem", marginTop: "0.5rem" }}>
                {formatTime(duration)}
              </div>
            </div>
          </>
        ) : (
          <>
            <div>
              <Icon name="file" size="big" />
            </div>
            <div>
              <div className="text" data-file-name={fileName}>
                Click to open file
              </div>
              {props.showFileSize && <DisplayFileSize file={props.file} />}
            </div>
          </>
        )}
      </div>

      <Modal
        className="chat-box-content-video-viewer"
        open={!!video}
        closeOnDimmerClick
        onClick={handleClose}
        onClose={handleClose}
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <video src={video || ""} autoPlay controls />
        </div>
        <div className="btn-close" onClick={handleClose}>
          <img alt="close" src={IMAGES.close} />
        </div>
      </Modal>
    </>
  );
};

export default DisplayContentFile;
